import { useCallback, useRef, useState, type ReactNode } from "react";
import { X } from "lucide-react";
import { Food } from "@corpuscomplete/core/nutrition/food";
import { Nutrient } from "@corpuscomplete/core/nutrition/nutrient";
import { formatNumber } from "@corpuscomplete/core/utils";
import { FoodUI } from "@corpuscomplete/core-ui/nutrition/food";
import { Button } from "@corpuscomplete/web-ui/components/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@corpuscomplete/web-ui/components/dropdown-menu";
import { Input } from "@corpuscomplete/web-ui/components/input";
import { Item, ItemActions, ItemContent, ItemTitle } from "@corpuscomplete/web-ui/components/item";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { cn } from "@corpuscomplete/web-ui/lib/utils";
import { FoodIcon } from "../FoodIcon";

/**
 * Generic food row primitive with optional select/remove affordances.
 * Contract: emits intent callbacks only; all nutrition and persistence rules
 * remain in caller-owned core/core-ui boundaries.
 */
export function FoodItem({
  item,
  onSelect,
  onSelectMethod = "onPressIn",
  onRemove,
  className,
  titleLines = 1,
}: FoodUI.FoodItemProps & { titleLines?: 1 | 2 }) {
  const itemContent = (
    <>
      <ItemContent>
        <FoodItemLabel item={item} titleLines={titleLines} />
      </ItemContent>
      {onRemove && (
        <ItemActions className="self-start pt-0.5">
          <Button
            onClick={onRemove}
            variant="ghost"
            size="icon-sm"
            className={cn("text-muted-foreground hover:bg-muted hover:text-foreground")}
          >
            <X className="size-4" />
          </Button>
        </ItemActions>
      )}
    </>
  );

  if (onSelect) {
    return (
      <Item
        variant="default"
        size="xs"
        className={cn(
          "flex-nowrap cursor-pointer rounded-lg border-transparent bg-transparent px-2 py-1.5 shadow-none [&:hover:not(:has(button:hover))]:bg-muted/40",
          className,
        )}
        onClick={onSelectMethod === "onPress" ? onSelect : undefined}
        onMouseDown={onSelectMethod === "onPressIn" ? onSelect : undefined}
      >
        {itemContent}
      </Item>
    );
  }

  return (
    <Item
      variant="default"
      size="xs"
      className={cn(
        "flex-nowrap rounded-lg border-transparent bg-transparent px-2 py-1.5 shadow-none",
        className,
      )}
    >
      {itemContent}
    </Item>
  );
}

/**
 * Reusable text/media block for a Food.Item.
 * Contract: caller controls serving presentation via optional renderServing slot.
 */
export function FoodItemLabel({
  item,
  renderServing,
  mode = "default",
  titleLines = 1,
}: {
  item: Food.Item;
  renderServing?: (args: { amountText: string; servingText: string }) => ReactNode;
  mode?: "default" | "preparation";
  titleLines?: 1 | 2;
}) {
  const { title } = Food.formatItem(item);
  const preparationTitle =
    item.food.modifiers.length > 0
      ? Food.formatModifiers(item.food.modifiers)
      : "Default preparation";
  const iconSlug = item.food.iconSlugs?.[0] ?? null;
  const amountText = formatNumber(item.amount);
  const servingText = Food.formatServing(item.serving);

  return (
    <div className="flex min-w-0 items-start gap-2.5">
      <FoodIcon slug={iconSlug} size="sm" className="mt-0.5 size-7 shrink-0 rounded-sm" />
      <div className="min-w-0 flex-1">
        {mode === "preparation" ? (
          <ItemTitle className="w-full min-w-0 line-clamp-1">{preparationTitle}</ItemTitle>
        ) : (
          <div className="flex min-w-0 items-start gap-1.5">
            <ItemTitle
              className={cn("min-w-0 flex-1", titleLines === 2 ? "line-clamp-2" : "line-clamp-1")}
            >
              {title}
            </ItemTitle>
            <FoodModifiersLabel modifiers={item.food.modifiers} />
          </div>
        )}
        <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-muted-foreground">
          {renderServing ? (
            renderServing({ amountText, servingText })
          ) : (
            <FoodServingLabel amountText={amountText} servingText={servingText} />
          )}
          <FoodMetaSeparator className="shrink-0" />
          <DenseNutrientSummary item={item} className="shrink-0" />
          <div className="min-w-0 flex-1" />
        </div>
      </div>
    </div>
  );
}

/**
 * Editable food-item row used by recipe and food-log entry forms.
 * Contract: emits numeric amount/serving changes to caller and remains
 * stateless with respect to mutation persistence.
 */
export function FoodItemInputView({
  className,
  itemInput,
  onEditItemAmount,
  onEditItemServing,
  onRemoveItem,
  titleLines = 1,
}: {
  className?: string;
  itemInput: Food.ItemInput;
  onEditItemAmount: (amount: number) => void;
  onEditItemServing: (serving: Food.Serving) => void;
  onRemoveItem: () => void;
  titleLines?: 1 | 2;
}) {
  const amountInputRef = useRef<HTMLInputElement>(null);
  const [isAmountFocused, setIsAmountFocused] = useState(false);
  const { servings, item, amountInput, onChangeAmountInput } = FoodUI.useItemInput({
    itemInput,
    onEditItemAmount,
    focusAmountInput: useCallback(() => amountInputRef.current?.focus(), []),
  });
  const amountDisplayValue = isAmountFocused
    ? amountInput
    : formatNumber(item?.amount ?? itemInput.amount);
  const amountInputWidth = getAmountInputWidth(amountDisplayValue);
  const focusAmountInputFromRow = useCallback((event: React.MouseEvent<HTMLElement>) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }

    if (target.closest("[data-no-focus-amount]")) {
      return;
    }

    amountInputRef.current?.focus();
  }, []);

  return (
    <Item
      variant="default"
      className={cn(
        "flex-nowrap rounded-lg border-transparent bg-transparent px-2 py-1.5 shadow-none [&:hover:not(:has(button:hover))]:bg-muted/40",
        className,
      )}
      size="xs"
      onClick={focusAmountInputFromRow}
    >
      <ItemContent>
        {item ? (
          <FoodItemLabel
            item={item}
            titleLines={titleLines}
            renderServing={({ servingText }) => (
              <div className="flex min-w-0 shrink items-baseline gap-1 overflow-hidden">
                <Input
                  data-no-focus-amount
                  data-no-drag
                  onPointerDown={(e) => e.stopPropagation()}
                  variant="ghost"
                  textVariant="xs"
                  ref={amountInputRef}
                  value={amountDisplayValue}
                  className="h-4 min-w-0 px-0 py-0 text-muted-foreground"
                  style={{ width: amountInputWidth }}
                  onChange={(e) => onChangeAmountInput(e.target.value)}
                  onFocus={() => {
                    setIsAmountFocused(true);
                  }}
                  onBlur={() => setIsAmountFocused(false)}
                />
                <DropdownMenu>
                  <DropdownMenuTrigger
                    className="min-w-0 overflow-hidden"
                    data-no-focus-amount
                    data-no-drag
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Text
                      variant="xs"
                      title={servingText}
                      className="block min-w-0 max-w-full truncate text-muted-foreground underline-offset-2 hover:cursor-default hover:underline"
                    >
                      {servingText}
                    </Text>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    {servings.length === 0 && (
                      <DropdownMenuItem>
                        <Text variant="xs">Loading...</Text>
                      </DropdownMenuItem>
                    )}
                    {servings.map((serving) =>
                      serving.id !== itemInput.foodServingId ? (
                        <DropdownMenuItem
                          key={serving.id}
                          onClick={() => onEditItemServing(serving)}
                        >
                          <Text variant="xs">{Food.formatServing(serving)}</Text>
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuCheckboxItem key={serving.id} checked>
                          <Text variant="xs">{Food.formatServing(serving)}</Text>
                        </DropdownMenuCheckboxItem>
                      ),
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          />
        ) : (
          <Text variant="xs">Loading...</Text>
        )}
      </ItemContent>
      <ItemActions className="self-start pt-0.5">
        <Button
          data-no-focus-amount
          data-no-drag
          onPointerDown={(e) => e.stopPropagation()}
          onClick={onRemoveItem}
          variant="ghost"
          size="icon-sm"
          className="text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="size-4" />
        </Button>
      </ItemActions>
    </Item>
  );
}

function FoodServingLabel({
  amountText,
  servingText,
}: {
  amountText: string;
  servingText: string;
}) {
  return (
    <div className="flex min-w-0 shrink items-baseline gap-1 overflow-hidden">
      <Text variant="xs" className="shrink-0 text-muted-foreground">
        {amountText}
      </Text>
      <Text variant="xs" title={servingText} className="truncate text-muted-foreground">
        {servingText}
      </Text>
    </div>
  );
}

function FoodMetaSeparator({ className }: { className?: string }) {
  return (
    <Text variant="xs" className={cn("text-muted-foreground", className)}>
      •
    </Text>
  );
}

function DenseNutrientSummary({ item, className }: { item: Food.Item; className?: string }) {
  const amounts = Nutrient.addFoodItems(Nutrient.emptyAmounts(), [item]);
  const parts = [`${Math.round(amounts.energy ?? 0)}kcal`];

  if ((amounts.carbohydrate ?? 0) > 0) {
    parts.push(`${Math.round(amounts.carbohydrate ?? 0)}C`);
  }

  if ((amounts.protein ?? 0) > 0) {
    parts.push(`${Math.round(amounts.protein ?? 0)}P`);
  }

  if ((amounts.fat ?? 0) > 0) {
    parts.push(`${Math.round(amounts.fat ?? 0)}F`);
  }

  return (
    <Text
      variant="xs"
      className={cn("whitespace-nowrap tabular-nums text-muted-foreground", className)}
    >
      {parts.join(" ")}
    </Text>
  );
}

function FoodModifiersLabel({ modifiers }: { modifiers: readonly Food.Modifier[] }) {
  if (modifiers.length === 0) {
    return null;
  }

  return (
    <Text variant="xs" className="min-w-0 shrink truncate text-muted-foreground">
      {Food.formatModifiers(modifiers)}
    </Text>
  );
}

function getAmountInputWidth(value: string): string {
  const compactLength = value.replace(/\s+/gu, "").length;
  const clampedLength = Math.min(Math.max(compactLength, 2), 6);
  return `${clampedLength * 0.84}ch`;
}
